import { useEffect, useState } from "react";
import Auth from "../../Auth";
import firebase from "../../Firebase";
import { useAdminAccessStore } from "../../stores/adminAccessStore";
import { isAdminSessionUid } from "../../../../src/shared/adminSessionIdentity";

/** A customer session must never look like the admin member's own workspace. */
export default function AdminSessionBanner() {
  const [authUser, setAuthUser] = useState(() => firebase.auth().currentUser);
  const accessAuthUid = useAdminAccessStore((state) => state.authUid);
  const session = useAdminAccessStore((state) => state.impersonation);
  const target = useAdminAccessStore((state) => state.effectiveUser);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState("");
  useEffect(
    () =>
      firebase.auth().onAuthStateChanged((user) => {
        setAuthUser(user);
        setEnding(false);
        setError("");
      }),
    [],
  );
  // The warning belongs to the Firebase credential, not its latest capability
  // lookup. An unavailable/expired lookup must never hide an active admin login.
  if (!authUser || !isAdminSessionUid(authUser.uid)) return null;
  const hasDetails = accessAuthUid === authUser.uid && session && target;
  const endSession = async () => {
    const auth = firebase.auth();
    if (auth.currentUser !== authUser) return;
    setEnding(true);
    setError("");
    try {
      await Auth.fetchWithIdToken("/api/admin-roles/impersonation/end", { user: authUser });
      // A sign-in in another tab can replace the identity during either await.
      // Revoke only the captured session and leave a newer login untouched.
      if (auth.currentUser !== authUser) return;
      Auth.markLogoutInProgress();
      await auth.signOut();
      if (auth.currentUser) return;
      useAdminAccessStore.getState().reset();
      window.location.href = "/";
    } catch {
      if (auth.currentUser !== authUser) return;
      setError("Unable to end the session. Please try again.");
      setEnding(false);
    }
  };
  return (
    <aside
      role="status"
      className="fixed bottom-0 left-0 right-0 z-[10000] bg-amber-100 text-amber-950 border-t border-amber-400 px-4 py-2 text-sm flex flex-wrap items-center justify-center gap-3"
    >
      {hasDetails ? (
        <>
          <strong>Viewing as {target.email || target.uid}</strong>
          <span>Admin actor: {session.actorId}</span>
          <span>Expires {new Date(session.expiresAt).toLocaleTimeString()}</span>
        </>
      ) : (
        <strong>Admin session active. Session details are unavailable.</strong>
      )}
      <span>End this session before using admin tools.</span>
      <button
        type="button"
        disabled={ending}
        onClick={endSession}
        className="rounded border border-amber-700 px-3 py-1 font-semibold"
      >
        {ending ? "Ending…" : "End session and sign out"}
      </button>
      {error && <span role="alert">{error}</span>}
    </aside>
  );
}
